import Heading from 'LuxKit/Typography/Heading'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import ExperienceTileSkeletonLoader from '../ExperienceTiles/ExperienceTileSkeletonLoader'
import { fillArray, take } from 'lib/array/arrayUtils'
import ExperienceTile from '../ExperienceTiles/ExperienceTile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferList from 'hooks/Offers/useOfferList'
import useExperiences from 'hooks/Experiences/useExperiences'
import {
  SORTBY_VERTICAL_EXPERIENCE_ADELAIDE,
  SORTBY_VERTICAL_EXPERIENCE_BRISBANE,
  SORTBY_VERTICAL_EXPERIENCE_MELBOURNE,
  SORTBY_VERTICAL_EXPERIENCE_PERTH,
  SORTBY_VERTICAL_EXPERIENCE_SYDNEY,
} from 'constants/offerList'
import { usePlaceAncestorsById } from 'hooks/usePlace'

const TOP_OFFERS_LIMIT = 3
const ListCount = fillArray(TOP_OFFERS_LIMIT)

const PLACE_TO_EXPERIENCES_SORT_BY: Record<string, App.OfferListSortByOptions> = {
  Sydney: SORTBY_VERTICAL_EXPERIENCE_SYDNEY,
  Melbourne: SORTBY_VERTICAL_EXPERIENCE_MELBOURNE,
  Brisbane: SORTBY_VERTICAL_EXPERIENCE_BRISBANE,
  Adelaide: SORTBY_VERTICAL_EXPERIENCE_ADELAIDE,
  Perth: SORTBY_VERTICAL_EXPERIENCE_PERTH,
}

interface Props {
  place: App.Place
}

function ExperiencesPageFeaturedCarousel(props: Props) {
  const { place } = props
  const intl = useIntl()
  const scrollElementRef = useRef<HTMLDivElement>(null)
  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(place.id)

  useEffect(() => {
    // if we change our place, we're looking at a "new list"
    // so take the carousel back to the start
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollLeft = 0
    }
  }, [place.id])

  const fallbackUnifiedResult = useOfferList({
    offerTypes: ['flash', 'always_on', 'channel_manager'],
    limit: TOP_OFFERS_LIMIT,
    sortBy: 'vertical-experience',
    placeIds: [place.id],
  })

  const specificLocationList = PLACE_TO_EXPERIENCES_SORT_BY[place.name]
  const unifiedResult = useOfferList({
    offerTypes: ['flash', 'always_on', 'channel_manager'],
    limit: TOP_OFFERS_LIMIT,
    sortBy: specificLocationList,
    placeIds: ancestorPlaceIds.length > 0 ? [ancestorPlaceIds[0]] : undefined,
  }, { disabled: !specificLocationList || fetchingAncestors })

  const [unifiedOffers, fetchingUnifiedOffers] = useExperiences(unifiedResult.offerIds ?? [])
  const [fallbackExperiences, fetchingFallbackExperiences] = useExperiences(fallbackUnifiedResult.offerIds ?? [])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Experiences Top Three Carousel'),
    { key: place.id },
  )

  const listFetching = fallbackUnifiedResult?.fetching || unifiedResult?.fetching || fetchingUnifiedOffers || fetchingFallbackExperiences || fetchingAncestors
  const expViews = useMemo(() => take([...unifiedOffers, ...fallbackExperiences], TOP_OFFERS_LIMIT).map(exp => exp ? mapExperienceToBookingView(exp) : undefined), [fallbackExperiences, unifiedOffers])

  return (
    <VerticalSpacer gap={24}>
      <Heading variant="heading3">
        {intl.formatMessage({
          id: 'aYTgXv9dU15oiG058HhH',
          defaultMessage: 'Our top {count} experiences',
          description: 'Heading for the top featured experiences carousel on experiences landing page',
        }, { count: TOP_OFFERS_LIMIT })}
      </Heading>
      <div>
        <OfferListEventsProvider tracking={tracking}>
          <CardCarousel pageSize={3} width="75%" ref={scrollElementRef}>
            {listFetching && ListCount.map((_, index) => <ExperienceTileSkeletonLoader key={index} />)}
            {!listFetching && expViews.map((expView, index) => <Fragment key={expView?.id ?? index}>
              {!expView && <ExperienceTileSkeletonLoader />}
              {expView && <ExperienceTile
                key={expView.id}
                to={getExperienceOfferPageURL(expView.originalExperience)}
                experience={expView}
                position={index}
              />}
            </Fragment>,
            )}
          </CardCarousel>
        </OfferListEventsProvider>
      </div>
    </VerticalSpacer>
  )
}

export default ExperiencesPageFeaturedCarousel
